import { ScheduleEntryState, Task, Timeslot, Timestamp } from '@griply/types';
import {
  createPlannedOccurrenceForDate,
  getScheduleByDate,
} from '@griply/utils';
import { isAfter, isSameDay } from 'date-fns';
import { useTaskTodayControls, useUpdateTask } from 'features/tasks';
import React, { useCallback, useRef, useState } from 'react';
import { Calendar16, MoreHorizontal, Repeat16 } from 'shared/assets/icons';
import { DatepickerWithOptions } from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { TimeRangeContextMenu } from 'shared/components/ui/time-range-context-menu';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';

import { useData } from '../../use-data';
import { DayEntry } from '../upcoming/types';
import { ActionMenu } from './action-menu';
import * as Styled from './upcoming-slot.style';
import { useSlotData } from './use-slot-data';

enum MenuOptions {
  Date = 'date',
  Time = 'time',
  Default = 'default',
}

export type EntrySlotProps = {
  task: Task;
  date: Timestamp;
  entry: DayEntry;
};

export const EntrySlot: React.FC<EntrySlotProps> = ({ task, date, entry }) => {
  const today = useToday();
  const { weekStartsOn, timeFormat, dateFormat } = useData();

  const actionMenuButtonRef = useRef<HTMLButtonElement>(null);
  const timeButtonRef = useRef<HTMLButtonElement>(null);

  const {
    position: contextMenuPosition,
    open: openContextMenu,
    close: closeContextMenu,
  } = useContextMenuState();

  const [menuOpen, setMenuOpen] = useState<MenuOptions>();
  const openTimeMenu = () => setMenuOpen(MenuOptions.Time);
  const openDefaultMenu = () => setMenuOpen(MenuOptions.Default);
  const openDateMenu = () => setMenuOpen(MenuOptions.Date);
  const closeMenu = () => {
    setMenuOpen(undefined);
    closeContextMenu();
  };

  const onContextMenuOpen = (e: React.MouseEvent) => {
    openContextMenu(e);
    setMenuOpen(MenuOptions.Default);
  };

  const { onComplete } = useTaskTodayControls();
  const { submit: updateTask } = useUpdateTask();

  const {
    isToday,
    label,
    checked,
    completionPercentage,
    isFuture,
    overdue,
    target,
    timeLabel,
  } = useSlotData({ task, date, entry });

  const _onComplete = useCallback(() => {
    if (
      task.schedules?.length &&
      !isSameDay(date, today) &&
      isAfter(date, today)
    ) {
      return;
    }

    const daySchedule = getScheduleByDate(task.schedules, { date, today });
    onComplete(task, ScheduleEntryState.Complete, {
      referenceDate: date,
      toggleDayEntry: !!daySchedule?.frequency,
      keepDetailOpen: true,
    });
  }, [task, date, today, onComplete]);

  const onChangeTimeslot = (timeslot: Timeslot | null) => {
    if (!task.schedules?.length) {
      return;
    }

    const schedules = createPlannedOccurrenceForDate(
      task.schedules,
      entry.date,
      {
        timeslot,
        today,
        weekStartsOn,
        id: entry.id,
        originalDate: entry.date,
      },
    );

    updateTask({ id: task.id, schedules });
  };

  const onChangeDate = (newDate?: Timestamp) => {
    if (!task.schedules?.length || !newDate) {
      return;
    }

    const schedules = createPlannedOccurrenceForDate(task.schedules, newDate, {
      today,
      weekStartsOn,
      originalDate: date,
    });

    updateTask({ id: task.id, schedules });
    closeMenu();
  };

  return (
    <Styled.Container as="div" onContextMenu={onContextMenuOpen}>
      {!isFuture || isToday ? (
        <TaskDetailCheckbox
          checked={checked}
          name={task.name}
          type={task.type}
          completionPercentage={completionPercentage}
          target={target}
          priority={task?.priority}
          onChange={_onComplete}
          isFrozen={!!task?.frozenAt}
          icon={entry.isGeneratedFrequencyEntry ? Repeat16 : Calendar16}
        />
      ) : (
        <Styled.StartIconContainer>
          <Icon
            icon={entry.isGeneratedFrequencyEntry ? Repeat16 : Calendar16}
          />
        </Styled.StartIconContainer>
      )}
      <Styled.LabelContainer $overdue={overdue} $hasEntry $isToday={isToday}>
        <Styled.LabelWrapper>
          <Styled.LabelText>
            {label}
            {entry?.hidden ? ' (hs)' : ''}
          </Styled.LabelText>
        </Styled.LabelWrapper>
      </Styled.LabelContainer>

      <Styled.EndContainer>
        <Styled.TimeButton
          ref={timeButtonRef}
          type="button"
          onClick={openTimeMenu}
        >
          {timeLabel}
        </Styled.TimeButton>
        <Tooltip label={timeLabel} referenceElement={timeButtonRef} />
        <IconButton
          icon={MoreHorizontal}
          onClick={openDefaultMenu}
          color={Colors.Default}
          size={Sizes.Small}
          ref={actionMenuButtonRef}
        />
        {menuOpen === MenuOptions.Date && (
          <DatepickerWithOptions
            value={date}
            onChange={onChangeDate}
            referenceElement={actionMenuButtonRef}
            onOutside={closeMenu}
            dateFormat={dateFormat}
            weekStartsOn={weekStartsOn}
            canRemoveValue={false}
          />
        )}
        {menuOpen === MenuOptions.Default && (
          <ActionMenu
            task={task}
            date={date}
            timeLabel={timeLabel}
            onDate={openDateMenu}
            onTime={openTimeMenu}
            referenceElement={
              contextMenuPosition ? undefined : actionMenuButtonRef
            }
            location={contextMenuPosition}
            onOutside={closeMenu}
            entryBased={entry.isUnplannedEntry}
            isGeneratedFrequencyEntry={entry.isGeneratedFrequencyEntry}
          />
        )}

        {menuOpen === MenuOptions.Time && (
          <TimeRangeContextMenu
            referenceElement={timeButtonRef}
            onChange={onChangeTimeslot}
            onClose={closeMenu}
            timeFormat={timeFormat}
            value={entry.timeslot}
          />
        )}
      </Styled.EndContainer>
    </Styled.Container>
  );
};
